/**
 * Bündelt alle Daten zum Partnersystem. Also Unternehmen, Personen wie Ansprechpartner (nicht Nutzer!) sowie ihrer
 * grundlegenden Datentypen wie Telefonnummer etc.
 * Nutzer (die in Keycloak registriert sind) werden in Nutzer.ts gepflegt, referenzieren aber auch hierauf (z.B. für Telefonnummer)
 */

import type { Buerge } from '@/shared/Buergen/types';
import type { Ansprechpartner, AuftraggeberTyp } from '@/types/Bürgschaft';
import { createEmptyAnsprechpartner } from '@/types/Bürgschaft';
import { ladeLaenderStorage } from '@/persistence/LänderStorage';
import type { PreisModell } from '@/shared/useFeatureToggles/preisModell';

// --- Basis ---

/** Eine Postadresse */
export interface Postadresse {
  straße: string;
  hausnummer: string;
  postleitzahl: string;
  ort: string;
  landeskennzeichen: string;
}

export function createEmptyPostadresse(): Postadresse {
  return {
    straße: '',
    hausnummer: '',
    postleitzahl: '',
    ort: '',
    landeskennzeichen: 'DE',
  };
}

export function isPostadresseBefüllt(postadresse: Postadresse): boolean {
  return (
    postadresse &&
    ((postadresse.straße && postadresse.straße.trim() !== '') ||
      (postadresse.hausnummer && postadresse.hausnummer.trim() !== '') ||
      (postadresse.postleitzahl && postadresse.postleitzahl.trim() !== '') ||
      (postadresse.ort && postadresse.ort.trim() !== ''))
  );
}

export function isPostadresseVollständigBefüllt(postadresse: Postadresse): boolean {
  return (
    postadresse &&
    postadresse.straße?.trim() !== '' &&
    postadresse.hausnummer?.trim() !== '' &&
    postadresse.postleitzahl?.trim() !== '' &&
    postadresse.ort?.trim() !== ''
  );
}

// --- Bürgschafts-Beteiligte ---

export const enum VersandOption {
  AUFTRAGNEHMER = 'auftragnehmer',
  AUFTRAGNEHMER_MUTTER = 'auftragnehmer-mutter',
  AUFTRAGGEBER = 'auftraggeber',
  ANDERE = 'andere',
}

/** Die Versandadresse für Papierbürgschaften */
export interface Versandadresse {
  name: string;
  zustellanweisung: string;
  postadresse: Postadresse;
}

/** Der Auftragnehmer ist der Schuldner im Bürgschaftsvertrag */
export interface Auftragnehmer {
  auftragnehmerId?: string;
  fremdId: string;
  name: string;
  postadresse: Postadresse;
  anzahlToechter?: number;
  ansprechpartner?: Ansprechpartner;
}

/** Der Auftraggeber ist der Gläubiger im Bürgschaftsvertrag */
export interface Auftraggeber {
  auftraggeberId?: string | null;
  name: string;
  postadresse: Postadresse;
  versandadresse?: Versandadresse | null;
  oeffentlicherAuftraggeber?: boolean;
  vertretungsformel?: string[];
  partnertyp: AuftraggeberTyp | null;
  ansprechpartner?: Ansprechpartner | null;
}

/** Das Bauvorhaben beschreibt den Ort der zu erbringenden Leistung */
export interface Bauvorhaben {
  adresse?: Postadresse;
  freitext?: string;
}

export function createEmptyVersandadresse(): Versandadresse {
  return {
    name: '',
    zustellanweisung: '',
    postadresse: createEmptyPostadresse(),
  };
}

export function createEmptyAuftraggeber(): Auftraggeber {
  return {
    auftraggeberId: null,
    name: '',
    postadresse: createEmptyPostadresse(),
    versandadresse: createEmptyVersandadresse(),
    oeffentlicherAuftraggeber: false,
    vertretungsformel: [],
    partnertyp: null,
  };
}

export function createEmptyAuftragnehmer(): Auftragnehmer {
  return {
    auftragnehmerId: null,
    fremdId: '',
    name: '',
    postadresse: createEmptyPostadresse(),
    ansprechpartner: createEmptyAnsprechpartner(),
  };
}

// --- Unternehmen ---

/** Der Auftraggeber ist der Gläubiger im Bürgschaftsvertrag */
export interface BasisUnternehmen {
  unternehmenId: string;
  name: string;
  postadresse: Postadresse;
  ansprechpartner?: Ansprechpartner;
}

/** Ganz abstrakt: eine Firma. Kann in der BSP ein Auftraggeber oder ein Bürge sein. */
export interface Unternehmen extends BasisUnternehmen {
  version: number;
  gesellschaftsform: Gesellschaftsform;
  handelsregisternummer: string;
  handelsregister: string;
  person: Person;
  mutterUnternehmen: Unternehmen;
  mutterUnternehmenId: string;
  tochterUnternehmen: Unternehmen[];
  unternehmensstatus: Unternehmensstatus;
  hybrid: boolean;
  oeffentlicherAuftraggeber: boolean;
  bestaetigungsanfrageBuerge: boolean;
  inkognito: boolean;
  erstelltAm: string;
  fremdId?: string;
  preisModell?: keyof typeof PreisModell;
}

export function createEmptyUnternehmen(): Unternehmen {
  return {
    unternehmenId: null,
    version: null,
    name: '',
    fremdId: '',
    gesellschaftsform: null,
    postadresse: createEmptyPostadresse(),
    handelsregisternummer: '',
    handelsregister: '',
    person: null,
    mutterUnternehmen: null,
    mutterUnternehmenId: '',
    tochterUnternehmen: [],
    unternehmensstatus: null,
    inkognito: false,
    hybrid: false,
    oeffentlicherAuftraggeber: false,
    bestaetigungsanfrageBuerge: false,
    erstelltAm: null,
  };
}

export function createEmptyUnternehmenMitVertretungsberechtigtem(): Unternehmen {
  return { ...createEmptyUnternehmen(), person: createEmptyPerson(Personentyp.VB) };
}

export enum Unternehmensstatus {
  UP = 'UP',
  AKT = 'AKT',
  DAK = 'DAK',
}

export enum UnternehmensstatusVerträge {
  UP = 'UP',
  AKT = 'AKT',
}

export const UnternehmenStatusAnzeigetextMap: Map<Unternehmensstatus, string> = new Map([
  [Unternehmensstatus.UP, 'Ungeprüft'],
  [Unternehmensstatus.AKT, 'Aktiv'],
  [Unternehmensstatus.DAK, 'Inaktiv'],
]);

export const UnternehmenStatusVerträgeAnzeigetextMap: Map<UnternehmensstatusVerträge, string> =
  new Map([
    [UnternehmensstatusVerträge.UP, 'Ungeprüft'],
    [UnternehmensstatusVerträge.AKT, 'Aktiv'],
  ]);

export enum Gesellschaftsform {
  AÖR = 'AÖR',
  AG = 'AG',
  ARGE = 'ARGE',
  BEHOERDE = 'BEHOERDE',
  EG = 'EG',
  EK = 'EK',
  EU = 'EU',
  EV = 'EV',
  GBR = 'GBR',
  GMBH = 'GMBH',
  GMBH_UND_CO_KG = 'GMBH_UND_CO_KG',
  KG = 'KG',
  KG_AA = 'KG_AA',
  KÖR = 'KÖR',
  OHG = 'OHG',
  SA = 'SA',
  SARL = 'SARL',
  SE = 'SE',
  SE_UND_CO_KG = 'SE_UND_CO_KG',
  STIFTUNG_UND_CO_KG = 'STIFTUNG_UND_CO_KG',
  UG = 'UG',
  UG_UND_CO_KG = 'UG_UND_CO_KG',
  VVAG = 'VVAG',
  WEG = 'WEG',
}

export const GesellschaftsformAnzeigetextMap: Map<Gesellschaftsform, string> = new Map([
  [Gesellschaftsform.AÖR, 'AöR'],
  [Gesellschaftsform.AG, 'AG'],
  [Gesellschaftsform.ARGE, 'ARGE'],
  [Gesellschaftsform.BEHOERDE, 'Behörde'],
  [Gesellschaftsform.EU, 'Einzelunternehmen'],
  [Gesellschaftsform.EG, 'eG'],
  [Gesellschaftsform.EK, 'e. K.'],
  [Gesellschaftsform.EV, 'e. V.'],
  [Gesellschaftsform.GBR, 'GbR'],
  [Gesellschaftsform.GMBH, 'GmbH'],
  [Gesellschaftsform.GMBH_UND_CO_KG, 'GmbH & Co. KG'],
  [Gesellschaftsform.KG, 'KG'],
  [Gesellschaftsform.KG_AA, 'KGaA'],
  [Gesellschaftsform.KÖR, 'KöR'],
  [Gesellschaftsform.OHG, 'OHG'],
  [Gesellschaftsform.SA, 'S.A.'],
  [Gesellschaftsform.SARL, 'S.à r.l.'],
  [Gesellschaftsform.SE, 'SE'],
  [Gesellschaftsform.SE_UND_CO_KG, 'SE & Co. KG'],
  [Gesellschaftsform.STIFTUNG_UND_CO_KG, 'Stiftung & Co. KG'],
  [Gesellschaftsform.UG, 'UG (haftungsbeschränkt)'],
  [Gesellschaftsform.UG_UND_CO_KG, 'UG (haftungsbeschränkt) & Co. KG'],
  [Gesellschaftsform.VVAG, 'VVaG'],
  [Gesellschaftsform.WEG, 'WEG'],
]);

// --- Bürge ---

export interface BürgeAnzeigedaten {
  bürge: Buerge;
  cssHeaderColor: string;
  cssHeaderFontColor: string;
}

export enum Vertragsart {
  LIGHT = 'LIGHT',
  REGULAR = 'REGULAR',
}

export enum BuergeBausummeAngabe {
  BETRAG = 'BETRAG',
  PROZENT = 'PROZENT',
  KEINE_ANGABE = 'KEINE_ANGABE',
}

export function createEmptyBürge(): Buerge {
  return {
    unternehmenId: null,
    version: null,
    name: '',
    gesellschaftsform: null,
    postadresse: createEmptyPostadresse(),
    handelsregisternummer: '',
    handelsregister: '',
    person: createEmptyPerson(Personentyp.VB),
    unternehmensstatus: null,
    erstelltAm: null,
    kurzname: '',
    verwendungBürgschaftstexte: false,
    vertragsart: Vertragsart.LIGHT,
    initialen: '',
    bausummeAngabe: null,
    lieferkanalPdf: null,
    type: 'BuergeDto',
  };
}

export function createEmptyBürgeAnzeigedaten(): BürgeAnzeigedaten {
  return {
    bürge: createEmptyBürge(),
    cssHeaderColor: '',
    cssHeaderFontColor: '',
  };
}

export function getBuergenMitVerwendungTexte(bürgen: Buerge[]): Buerge[] {
  return bürgen?.filter((b) => b.verwendungBürgschaftstexte) ?? [];
}

export function getBürgenIDsMitVerwendungTexte(bürgen: Buerge[]): string[] {
  return getBuergenMitVerwendungTexte(bürgen).map((b) => b.unternehmenId);
}

// --- Person ---

export enum Personentyp {
  KP = 'KP',
  VB = 'VB',
  BL = 'BL',
  BH = 'BH',
}

export interface Person {
  persId: number;
  vorname: string;
  nachname: string;
  telefonnummer: string;
  email: string;
  personentyp: Personentyp;
}

export function createEmptyPerson(personentyp: Personentyp): Person {
  return {
    persId: null,
    vorname: '',
    nachname: '',
    telefonnummer: '',
    email: '',
    personentyp,
  };
}

export interface PersonInUnternehmen {
  person: Person;
  unternehmenId: string;
  version: number;
}

export function createEmptyPersonInUnternehmen(): PersonInUnternehmen {
  return {
    person: createEmptyPerson(Personentyp.KP),
    unternehmenId: '',
    version: 0,
  };
}

// --- Land ---

export interface Land {
  landeskennzeichen: string;
  vollerName: string;
  ländervorwahl: string;
}

export function asLand(landeskennzeichen: string): Land | null {
  return ladeLaenderStorage().länder.find((l) => l.landeskennzeichen === landeskennzeichen) ?? null;
}

export function getVollerNameFromLandeskennzeichen(landeskennzeichen: string): string | null {
  return asLand(landeskennzeichen)?.vollerName ?? null;
}

export function getPlzOrt(postadresse: Postadresse): string {
  if (!postadresse) {
    return '';
  }
  return `${postadresse.postleitzahl ?? ''} ${postadresse.ort ?? ''}`.trim();
}

export function getPlzOrtOhnePostadresse(postleitzahl: string, ort: string): string {
  return `${postleitzahl ?? ''} ${ort ?? ''}`.trim();
}

// --- Sonstiges ---

export interface NutzerAnlagePruefErgebnis {
  koennteAngelegtWerden: boolean;
  nutzerExistiertInEigenerOrganisation: boolean;
  nutzerKannWiederhergestelltWerden: boolean;
}

export interface EintrittskarteRequest {
  bürgschaftId: string;
  version: number;
  eintrittskartentyp: Eintrittskartentyp;
  personInUnternehmen: PersonInUnternehmen;
  hinweis: '';
  hashWert?: string;
}

export enum Eintrittskartentyp {
  EINSEHEN = 'EINSEHEN',
  KOMMENTIEREN = 'KOMMENTIEREN',
  FREIGABE_PRUEFEN = 'FREIGABE_PRUEFEN',
}
